/**
 * Authentication Type Definitions
 * TypeScript interfaces and types for authentication system
 */

// User Role Enum - Updated for 3-Role System
export enum UserRole {
  // New simplified roles
  ADMIN = 'ADMIN',
  MEMBER = 'MEMBER',

  // Legacy roles for backward compatibility (will be migrated)
  SUPER_ADMIN = 'SUPER_ADMIN',
  COMPANY_OWNER = 'COMPANY_OWNER',
  USER = 'USER'
}

// User Status Enum
export enum UserStatus {
  ACTIVE = 'ACTIVE',
  INACTIVE = 'INACTIVE',
  SUSPENDED = 'SUSPENDED',
  BANNED = 'BANNED'
}

// User Interface
export interface User {
  id: string;
  email: string;
  firstName?: string;
  lastName?: string;
  full_name: string;
  display_name: string;
  phone?: string;
  lineId?: string;
  avatarUrl?: string;
  userCategory?: string; // User category for personalization (business_owner, security_company, etc.)
  role: UserRole;
  status: UserStatus;
  isActive: boolean;
  // Email verification removed - users can login immediately
  // isEmailVerified: boolean;
  profileCompletionPercent?: number;
  lastLoginAt?: string;
  loginCount?: number;
  createdAt: string;
  updatedAt: string;
}

// Public User Interface (limited information)
export interface PublicUser {
  id: string;
  display_name: string;
  role?: UserRole;
  profile_completion: number;
  // Email verification removed
  // is_verified: boolean;
}

// Admin User Interface (includes private fields)
export interface AdminUser extends User {
  adminNotes?: string;
  // Email verification removed
  // emailVerifiedAt?: string;
}

// Login Credentials
export interface LoginCredentials {
  email: string;
  password: string;
  rememberMe?: boolean;
}

// Registration Data
export interface RegisterData {
  email: string;
  password: string;
  confirmPassword: string;
  firstName?: string;
  lastName?: string;
  phone?: string;
  lineId?: string;
  userCategory?: string; // User category for personalization
  role?: UserRole;
}

// Authentication Response
export interface AuthResponse {
  access_token: string;
  refresh_token: string;
  token_type: string;
  expires_in: number;
  user: User;
  message?: string;
}

// Login Response
export interface LoginResponse extends AuthResponse {
  message: string;
}

// Registration Response
export interface RegisterResponse {
  message: string;
  user: User;
  // Email verification removed - users can login immediately
  // verification_required: boolean;
}

// Password Change Data
export interface PasswordChangeData {
  currentPassword: string;
  newPassword: string;
  confirmNewPassword: string;
}

// Password Reset Data
export interface PasswordResetData {
  token: string;
  newPassword: string;
  confirmNewPassword: string;
}

// Refresh Token Response
export interface RefreshTokenResponse {
  access_token: string;
  token_type: string;
  expires_in: number;
}

// Profile Update Data
export interface ProfileUpdateData {
  firstName?: string;
  lastName?: string;
  phone?: string;
  lineId?: string;
}

// Email verification removed - users can login immediately
// export interface EmailVerificationData {
//   token: string;
// }

// Authentication Error
export interface AuthError {
  error: string;
  message: string;
  details?: Record<string, unknown>;
}

// Validation Error
export interface ValidationError {
  error: string;
  message: string;
  details: Array<{
    field: string;
    message: string;
  }>;
}

// Permission Types
export type Permission =
  | 'create_company'
  | 'edit_any_company'
  | 'delete_company'
  | 'verify_company'
  | 'manage_users'
  | 'ban_users'
  | 'view_admin_panel'
  | 'manage_system'
  | 'view_analytics'
  | 'approve_requests';

// Authentication State
export interface AuthState {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
  token: string | null;
}

// Authentication Context Props (Updated for 3-Role System)
export interface AuthContextProps {
  // State
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
  token: string | null;

  // Actions
  login: (credentials: LoginCredentials) => Promise<void>;
  register: (data: RegisterData) => Promise<void>;
  logout: () => void;
  updateProfile: (data: ProfileUpdateData) => Promise<void>;
  changePassword: (currentPassword: string, newPassword: string) => Promise<void>;
  refreshToken: () => Promise<void>;
  clearError: () => void;

  // Permission checks
  hasRole: (role: UserRole) => boolean;
  hasPermission: (permission: Permission) => boolean;
  canManageCompanies: () => boolean;

  // New 3-Role System helpers
  isAdmin: boolean;      // ADMIN or SUPER_ADMIN
  isMember: boolean;     // MEMBER, USER, COMPANY_OWNER, or ADMIN
  isGuest: boolean;      // Not authenticated (unauthenticated user)
}

// Form Validation Rules
export interface ValidationRule {
  required?: boolean;
  minLength?: number;
  maxLength?: number;
  pattern?: RegExp;
  custom?: (value: string) => string | null;
}

export interface ValidationRules {
  [field: string]: ValidationRule;
}

// Form Field Error
export interface FieldError {
  message: string;
  type: string;
}

// Form Errors
export interface FormErrors {
  [field: string]: FieldError;
}

// Password Strength
export interface PasswordStrength {
  score: number; // 0-4
  feedback: string[];
  isValid: boolean;
}

// Session Information
export interface SessionInfo {
  id: string;
  userId: string;
  expiresAt: string;
  isActive: boolean;
  ipAddress?: string;
  userAgent?: string;
  createdAt: string;
  remaining_time?: string;
}

// User Statistics (for admin)
export interface UserStats {
  total_users: number;
  active_users: number;
  // Email verification removed
  // verified_users: number;
  users_by_role: { [role: string]: number };
  recent_registrations: number;
  recent_logins: number;
}

// Admin User List Response
export interface UserListResponse {
  users: AdminUser[];
  total: number;
  page: number;
  per_page: number;
  pages: number;
}

// User Create Request (Admin)
export interface UserCreateRequest {
  email: string;
  password: string;
  firstName?: string;
  lastName?: string;
  phone?: string;
  lineId?: string;
  role: UserRole;
  status: UserStatus;
  // Email verification removed
  // isEmailVerified: boolean;
}

// User Update Request (Admin)
export interface UserUpdateRequest {
  email?: string;
  firstName?: string;
  lastName?: string;
  phone?: string;
  lineId?: string;
  role?: UserRole;
  status?: UserStatus;
  // Email verification removed
  // isEmailVerified?: boolean;
  adminNotes?: string;
}

// API Key Response
export interface APIKeyResponse {
  api_key: string;
  message: string;
}

// JWT Token Payload
export interface TokenPayload {
  sub: string; // user id
  email: string;
  role: string;
  session_id?: string;
  exp: number;
  iat: number;
  type: string;
}

// Route Protection Props
export interface ProtectedRouteProps {
  children: React.ReactNode;
  requiredRoles?: UserRole[];
  requiredPermissions?: Permission[];
  fallback?: React.ReactNode;
}

// Auth Hook Return Type
export interface UseAuthReturn extends AuthContextProps {}

// Login Form Data
export interface LoginFormData {
  email: string;
  password: string;
  rememberMe: boolean;
}

// Register Form Data
export interface RegisterFormData {
  email: string;
  password: string;
  confirmPassword: string;
  firstName: string;
  lastName: string;
  phone: string;
  lineId: string;
  userCategory: string; // User category selection (business_owner, security_company, etc.)
  acceptTerms: boolean;
}

// Profile Form Data
export interface ProfileFormData {
  firstName: string;
  lastName: string;
  phone: string;
  lineId: string;
}

// Password Change Form Data
export interface PasswordChangeFormData {
  currentPassword: string;
  newPassword: string;
  confirmNewPassword: string;
}

// Forgot Password Form Data
export interface ForgotPasswordFormData {
  email: string;
}

// Reset Password Form Data
export interface ResetPasswordFormData {
  token: string;
  newPassword: string;
  confirmNewPassword: string;
}